import { z } from 'zod';
import {
  booleanoDeQuerySchema,
  custoUnitarioSchema,
  dataSchema,
  dinheiroSchema,
  paginacaoSchema,
  quantidadeSchema,
  uuidSchema,
} from './comum.ts';

/**
 * Estoque de um depósito — **o que tem no galpão, hoje** (`.spec/016`, seção 9;
 * `.spec/010`, "Movimentação de estoque").
 *
 * É a leitura direta de `insumos_deposito` recortada por um local: uma linha por
 * material, com saldo, custo médio, valor e o último custo de compra. Nada é
 * reconstruído do histórico — os quatro números estão gravados, e é justamente
 * por isso que esta consulta é barata onde o cardex é cara.
 *
 * ## Por que não é o cardex nem a listagem de insumos
 *
 * As três leem estoque, e cada uma responde uma pergunta diferente:
 *
 * | Tela | Pergunta |
 * |---|---|
 * | Listagem de insumos | "quanto deste material o **grupo** tem, somado" |
 * | Cardex | "**o que aconteceu** com este material, movimento a movimento" |
 * | Esta | "**o que tem** neste galpão, material a material" |
 *
 * O cardex fixa um material e abre o tempo; esta fixa um local e abre o
 * catálogo. Nenhuma das duas se obtém da outra: o cardex de trezentos materiais
 * não tem coluna de saldo acumulado que faça sentido, e o inventário de um
 * depósito não tem histórico nenhum a mostrar.
 *
 * A alternativa avaliada era um filtro de depósito na listagem de insumos. Foi
 * descartada por confundir cadastro com consulta: aquela tela abre o registro ao
 * clicar na linha, tem "mostrar inativos" e ordena por nome de catálogo — e o
 * saldo dela é a **soma das empresas vinculadas**, que é outro número. Um filtro
 * ali faria a mesma coluna significar duas coisas conforme um campo estivesse
 * preenchido ou não.
 *
 * ## O depósito é obrigatório
 *
 * Sem ele isto é a listagem de insumos, que já existe. E o total em valor só
 * fecha com a contabilidade quando o recorte é um local: somar galpões de
 * empresas diferentes dá um número que nenhum balanço tem.
 */

/* -------------------------------------------------------------------------- */
/* Consulta                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Filtros.
 *
 * Herda `paginacaoSchema` — e não `listagemDeCadastroSchema` — de propósito: não
 * há filtro de situação a oferecer. O que a tela precisa saber sobre situação é
 * do **insumo**, e ele vem na linha como etiqueta: o material inativado ontem
 * com saldo dentro é exatamente o que o inventário precisa mostrar, e escondê-lo
 * faria o total da tela não bater com o do razão.
 *
 * `q` vem do `paginacaoSchema` e busca o nome do material, pelo mesmo `tsvector`
 * da listagem de insumos (`.spec/004`, seção 2).
 */
export const consultarEstoqueDoDepositoQuerySchema = paginacaoSchema.extend({
  /** O galpão. **Obrigatório** — é ele que define a pergunta. */
  depositoId: uuidSchema,
  /** Recorta o catálogo a um ramo da árvore de grupos, com os subgrupos abaixo. */
  grupoInsumoId: uuidSchema.optional(),
  /**
   * Só as linhas com saldo diferente de zero.
   *
   * **Ausente traz todas**, e a diferença importa nas duas direções. O
   * inventário de conferência quer só o que tem — a linha zerada é ruído entre
   * as trezentas do catálogo. Já quem procura "por que o cimento sumiu" precisa
   * da linha zerada, que é a prova de que o material esteve ali.
   *
   * "Diferente de zero", e não "maior que zero": saldo negativo existe quando a
   * saída passou do que havia (a política da conta permite), e é a linha que
   * mais precisa aparecer.
   */
  comSaldo: booleanoDeQuerySchema,
  /**
   * Só as linhas abaixo do ponto de reposição.
   *
   * `quantidade_estoque < quantidade_estoque_reposicao`, com a reposição maior
   * que zero — a linha sem reposição cadastrada não está "abaixo" de nada, e
   * incluí-la traria o catálogo inteiro sob um rótulo que promete o contrário.
   */
  aRepor: booleanoDeQuerySchema,
});

export type ConsultarEstoqueDoDepositoQuery = z.infer<typeof consultarEstoqueDoDepositoQuerySchema>;

/**
 * Colunas de ordenação.
 *
 * `valorEstoque` primeiro na intenção, `nome` no padrão: quem abre a tela
 * procura um material pelo nome, e quem confere o inventário ordena pelo valor
 * para ver onde está o dinheiro. `quantidade` ordena por número puro e mistura
 * unidades — é útil mesmo assim, para achar o extremo.
 */
export const COLUNAS_ORDENAVEIS_ESTOQUE_DEPOSITO = [
  'nome',
  'quantidade',
  'valorEstoque',
  'custoMedio',
] as const;

/* -------------------------------------------------------------------------- */
/* Resposta                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Uma linha do inventário: **um material neste depósito**.
 *
 * Os quatro números vêm gravados de `insumos_deposito`, sem conta nenhuma no
 * meio. É a diferença para a listagem de insumos, onde o custo médio é média
 * ponderada calculada no `SELECT` — aqui ele é o custo médio **do local**, que é
 * o que a saída consome.
 */
export const linhaDoEstoqueDoDepositoSchema = z.object({
  /** Id da linha `insumos_deposito`. A tabela não abre registro; serve de chave. */
  id: uuidSchema,
  insumoId: uuidSchema,
  insumoNome: z.string(),
  /** Inativo aparece, e aparece marcado: o saldo dele é do inventário. */
  insumoAtivo: z.boolean(),
  grupoNome: z.string().nullable(),
  unidadeSigla: z.string().nullable(),
  /** Decide se a quantidade se exibe com decimais. */
  unidadeApenasInteiros: z.boolean(),
  quantidade: quantidadeSchema,
  /** Ponto de reposição do material **neste** depósito. Zero é "não cadastrado". */
  quantidadeReposicao: quantidadeSchema,
  /** Seis casas: é a taxa que a saída multiplica. Ver `custoUnitarioSchema`. */
  custoMedio: custoUnitarioSchema,
  valorEstoque: dinheiroSchema,
  /**
   * O que a última entrada de nota custou, **neste depósito**.
   *
   * Ao lado do custo médio, e não no lugar dele: um diz o que o estoque vale,
   * o outro diz por quanto se compra hoje. A distância entre os dois é o que se
   * olha antes de aprovar uma cotação.
   */
  ultimoCusto: custoUnitarioSchema,
  /** Dia do último movimento desta linha, ou nulo quando nunca houve. */
  dataUltimoMovimento: dataSchema.nullable(),
});

export type LinhaDoEstoqueDoDeposito = z.infer<typeof linhaDoEstoqueDoDepositoSchema>;

/**
 * O inventário do depósito.
 *
 * O cabeçalho traz depósito e empresa resolvidos porque a tela e a exportação
 * precisam deles, e porque "Estoque Geral" sozinho não diz de qual empresa — no
 * grupo com três empresas há três galpões com esse nome.
 *
 * `valorEstoque` e `materiais` são do **recorte inteiro**, não da página. Somar
 * a página daria um total que muda ao virar de página, que é o defeito clássico
 * de totalizador em tabela paginada.
 */
export const estoqueDoDepositoSchema = z.object({
  depositoId: uuidSchema,
  depositoNome: z.string(),
  /** Depósito inativo tem inventário: ele não recebe movimento, mas guarda saldo. */
  depositoAtivo: z.boolean(),
  /** A obra, quando o depósito é o canteiro dela (`depositos.obra_id`). */
  obraId: uuidSchema.nullable(),
  obraNome: z.string().nullable(),
  empresaId: uuidSchema,
  empresaNome: z.string().nullable(),

  /** Quantos materiais o recorte tem — é o total da paginação. */
  total: z.number().int(),
  /**
   * Soma do valor em estoque de **todo o recorte**.
   *
   * Só o valor se soma. Quantidade não: trezentas linhas em saco, metro cúbico e
   * unidade não têm total nenhum, e um número ali seria plausível e sem
   * significado.
   */
  valorEstoque: dinheiroSchema,
  /**
   * Quantas linhas do recorte estão abaixo do ponto de reposição.
   *
   * Do recorte inteiro, como o valor — é o número que manda ligar o filtro
   * `aRepor`, e contá-lo só na página o faria mudar ao virar de página.
   */
  aRepor: z.number().int(),

  data: z.array(linhaDoEstoqueDoDepositoSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
});

export type EstoqueDoDeposito = z.infer<typeof estoqueDoDepositoSchema>;
